import * as React from 'react';
import { FunctionComponent, PropsWithChildren, ReactElement } from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import { InputAdornment } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import { useAppDispatch } from '../../../store/hooks';
import { BaseSelectWithCheckboxesProps } from './models/base-select-with-checkboxes.props';
import { getBaseSelectWithCheckboxesMenuProps } from './utils/get-base-select-with-checkboxes-menu-props/get-base-select-with-checkboxes-menu-props.util';

const BaseSelectWithCheckboxes: FunctionComponent<
  BaseSelectWithCheckboxesProps
> = (props: PropsWithChildren<BaseSelectWithCheckboxesProps>): ReactElement => {
  const {
    selectOptions,
    isLoading,
    clearMultiSelectFilterAction,
    ...selectProps
  } = props;
  const menuProps = getBaseSelectWithCheckboxesMenuProps();

  const dispatch = useAppDispatch();

  return (
    <Select
      classes={{
        filled: 'input-focused',
      }}
      className="dropdown-autocomplete"
      data-testid={'base-select-with-checkboxes'}
      inputProps={{
        classes: {
          focused: 'input-focused',
        },
      }}
      MenuProps={menuProps}
      multiple={true}
      renderValue={selected =>
        selectOptions
          .filter(opt => selected.includes(opt.id))
          .map(opt => opt.label)
          .join(', ')
      }
      sx={{
        width: '100%',
        height: '56px',
      }}
      value={selectProps.value}
      {...(selectProps.value &&
        selectProps.value.length > 0 &&
        clearMultiSelectFilterAction && {
          endAdornment: (
            <InputAdornment sx={{ marginRight: '20px' }} position="end">
              <IconButton
                onClick={() => dispatch(clearMultiSelectFilterAction([]))}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        })}
      {...selectProps}
    >
      {selectOptions.map(selectOption => (
        <MenuItem
          key={`${selectOption.id}-${selectOption.label}`}
          value={selectOption.id}
          disabled={isLoading}
          sx={{ pointerEvents: isLoading ? 'none' : 'auto' }}
        >
          <Checkbox
            checked={selectProps.value?.some(
              option => option === selectOption.id
            )}
            data-testid={'checkbox-container'}
          />
          <ListItemText
            primary={selectOption.label}
            data-testid={'checkbox-label'}
          />
        </MenuItem>
      ))}
    </Select>
  );
};

export default BaseSelectWithCheckboxes;
